import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiHeart, FiBook, FiTrendingUp, FiUsers, FiExternalLink, FiMail, FiCalendar, FiStar} = FiIcons;

function NextSteps() {
  const resources = [
    {
      title: 'Writing Tracker & Templates',
      description: 'Stay consistent with your writing goals using our comprehensive tracking system and StoryWise templates.',
      icon: FiBook,
      color: 'amber',
      features: ['Daily writing tracker', 'StoryWise fiction templates', 'Progress analytics', 'Goal setting tools'],
      cta: 'Get Writing Tools',
      link: 'https://www.gospelwiseauthor.com/product/ultimate-book-project-manager/'
    },
    {
      title: 'GospelWise Marketing Starter',
      description: 'Learn to market your book with authenticity and gospel wisdom. No hustle, just faithful service.',
      icon: FiTrendingUp,
      color: 'blue',
      features: ['15-minute marketing plan', 'Content templates', 'Platform building guide', 'Email marketing basics'],
      cta: 'Download Guide',
      link: 'https://www.crossandpen.com/resources/marketingchecklist/'
    },
    {
      title: 'Author Coaching & Community',
      description: 'Join a community of Christian authors and get personalized coaching to accelerate your journey.',
      icon: FiUsers,
      color: 'emerald',
      features: ['Monthly group coaching', 'Private author community', 'Manuscript feedback', 'Publishing guidance'],
      cta: 'Join Community',
      link: 'https://www.crossandpen.com/fellowship'
    }
  ];

  const courses = [
    {
      title: 'Write. Refine. Release',
      description: 'Complete course covering the entire journey from book concept to published author.',
      duration: '8 weeks',
      level: 'Beginner to Intermediate',
      link: 'https://www.crossandpen.com/wrr'
    },
    {
      title: 'GospelWise Marketing Mastery',
      description: 'Deep dive into marketing your book with authenticity and kingdom principles.',
      duration: '6 weeks',
      level: 'All levels',
      link: 'https://www.crossandpen.com/resources/essentials/'
    }
  ];

  const colorClasses = {
    amber: {
      bg: 'from-amber-50 to-yellow-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      button: 'bg-amber-600 hover:bg-amber-700'
    },
    blue: {
      bg: 'from-blue-50 to-indigo-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      button: 'bg-blue-600 hover:bg-blue-700'
    },
    emerald: {
      bg: 'from-emerald-50 to-green-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      button: 'bg-emerald-600 hover:bg-emerald-700'
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <SafeIcon icon={FiStar} className="w-16 h-16 text-amber-600 mx-auto mb-4" />
        <h1 className="font-serif text-4xl text-gray-800 mb-4">Continue Your Author Journey</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          You've taken the first faithful step. Now let's equip you with everything you need to write, publish, and share your book with confidence and gospel wisdom.
        </p>
      </div>

      {/* Free Resources */}
      <div className="mb-16">
        <h2 className="font-serif text-3xl text-gray-800 text-center mb-8">Free Resources to Get You Started</h2>
        <div className="grid lg:grid-cols-3 gap-8">
          {resources.map((resource, index) => {
            const colors = colorClasses[resource.color];
            return (
              <div key={index} className={`bg-gradient-to-br ${colors.bg} rounded-2xl p-6 border ${colors.border} hover:shadow-lg transition-all duration-200`}>
                <div className="mb-6">
                  <div className="bg-white rounded-xl p-3 inline-flex mb-4 shadow-sm">
                    <SafeIcon icon={resource.icon} className={`w-8 h-8 ${colors.text}`} />
                  </div>
                  <h3 className="font-serif text-xl text-gray-800 mb-3">{resource.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{resource.description}</p>
                </div>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">What's included:</h4>
                  <ul className="space-y-2">
                    {resource.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-sm text-gray-600">
                        <div className={`w-1.5 h-1.5 rounded-full bg-current ${colors.text}`}></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {resource.link === '#' ? (
                  <button className={`w-full ${colors.button} text-white py-3 px-4 rounded-xl transition-colors flex items-center justify-center space-x-2`}>
                    <span>{resource.cta}</span>
                    <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  </button>
                ) : (
                  <a 
                    href={resource.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full ${colors.button} text-white py-3 px-4 rounded-xl transition-colors flex items-center justify-center space-x-2`}
                  >
                    <span>{resource.cta}</span>
                    <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Courses */}
      <div className="mb-16">
        <h2 className="font-serif text-3xl text-gray-800 text-center mb-8">Deep Dive Courses</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {courses.map((course, index) => (
            <div key={index} className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-200">
              <h3 className="font-serif text-2xl text-gray-800 mb-3">{course.title}</h3>
              <p className="text-gray-600 leading-relaxed mb-4">{course.description}</p>
              <div className="flex items-center space-x-6 text-sm text-gray-500 mb-6">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiStar} className="w-4 h-4" />
                  <span>{course.level}</span>
                </div>
              </div>
              <a 
                href={course.link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 text-white py-3 px-6 rounded-xl hover:bg-gray-900 transition-colors flex items-center space-x-2"
              >
                <span>Learn More</span>
                <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Personal Touch */}
      <div className="bg-gradient-to-br from-amber-50 to-emerald-50 rounded-2xl p-8 border border-amber-200 text-center">
        <div className="mb-6">
          <div className="w-24 h-24 bg-gradient-to-br from-amber-200 to-emerald-200 rounded-full mx-auto mb-4 flex items-center justify-center">
            <span className="text-3xl font-serif text-gray-700">JB</span>
          </div>
          <h3 className="font-serif text-2xl text-gray-800 mb-2">Let's Walk This Road Together</h3>
          <p className="text-emerald-600 font-medium">Jeremy Bouma</p>
        </div>
        
        <div className="max-w-2xl mx-auto space-y-4 text-gray-700 leading-relaxed mb-6">
          <p>I believe God has given you a story worth telling and a message worth sharing. My heart is to come alongside you as you steward this calling with faithfulness and joy.</p>
          <p>Whether you're just starting or ready to publish, I'm here to help you take the next step with confidence, authenticity, and gospel wisdom.</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href="https://www.crossandpen.com/coaching/chat/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-emerald-600 text-white px-6 py-3 rounded-xl hover:bg-emerald-700 transition-colors flex items-center justify-center space-x-2"
          >
            <SafeIcon icon={FiMail} className="w-4 h-4" />
            <span>Schedule a Call</span>
          </a>
          <a 
            href="https://www.crossandpen.com/fellowship"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-amber-600 text-white px-6 py-3 rounded-xl hover:bg-amber-700 transition-colors flex items-center justify-center space-x-2"
          >
            <SafeIcon icon={FiUsers} className="w-4 h-4" />
            <span>Join Our Community</span>
          </a>
        </div>
      </div>

      {/* Final Encouragement */}
      <div className="text-center mt-12">
        <div className="bg-white/60 rounded-xl p-6 border border-gray-200">
          <p className="text-gray-700 italic text-lg leading-relaxed">
            "Your book is a calling worth pursuing. Take it one faithful step at a time, and trust God with the beautiful work He wants to do through your words."
          </p>
        </div>
      </div>
    </div>
  );
}

export default NextSteps;